import { Popover as DefaultPopover, Transition } from '@headlessui/react';
import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@restworld/utils-common';
export enum Trigger {
  CLICK = 'click',
  HOVER = 'hover',
}

export interface PopoverProps {
  children: React.ReactNode;
  trigger?: Trigger;
  buttonstring?: string;
  panelstring?: string;
  haveDefaultButtonStyle?: boolean;
  fullWidth?: boolean;
  popoverstring?: string;
}

/**
 * A customizable popover component that can be triggered by click or hover.
 * @param children - The content of the popover, which should include a Popover.Button and a Popover.Panel.
 * @param trigger - The trigger type for the popover. Defaults to Trigger.CLICK.
 * @param buttonstring - The Tailwind CSS styles to apply to the Popover.Button component.
 * @param panelstring - The Tailwind CSS styles to apply to the Popover.Panel component.
 * @param haveDefaultButtonStyle - Whether to apply the default button style to the Popover.Button component. Defaults to true.
 * @example
    import { Popover, Trigger } from './popover';
import { cn } from '@restworld/utils-common';

    const ExamplePopover = () => {
      return (
        <Popover trigger={Trigger.HOVER}>
          <Popover.Button>Hover me</Popover.Button>
          <Popover.Panel>
            <div className="p-4 bg-white shadow-md rounded-md">
              <p className="text-gray-800">Hello, world!</p>
            </div>
          </Popover.Panel>
        </Popover>
      );
    };
 */
export const Popover = ({
  children,
  trigger = Trigger.CLICK,
  buttonstring,
  panelstring,
  haveDefaultButtonStyle = true,
  fullWidth = false,
  popoverstring,
}: PopoverProps) => {
  const [show, setShow] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  let button: React.ReactNode | undefined = undefined;
  let panel: React.ReactNode | undefined = undefined;

  const handleClickOutside = (event: MouseEvent) => {
    if (
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node) &&
      panelRef.current &&
      !panelRef.current.contains(event.target as Node)
    ) {
      event.stopPropagation();
      setShow(false);
    }
  };
  const handleMouseEnter = () => {
    if (trigger === Trigger.HOVER) {
      setShow(true);
      buttonRef?.current?.click();
    }
  };

  const handleMouseLeave = () => {
    if (trigger === Trigger.HOVER) {
      setShow(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      handleClickOutside(event);
    };

    document?.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document?.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === Popover.Button) {
        button = child;
      } else if (child.type === Popover.Panel) {
        panel = child;
      }
    }
  });

  const handleClick = (open: boolean) => {
    setShow(!open);
  };
  return (
    <StyledPopover {...{ $fullWidth: fullWidth, $popoverstring: popoverstring }}>
      {({ open }) => (
        <StyledPopoverEntry $fullWidth={fullWidth} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          <StyledPopoverButton
            onClick={() => {
              trigger === Trigger.CLICK && handleClick(open);
            }}
            $buttonstring={buttonstring}
            $fullWidth={fullWidth}
            ref={buttonRef}
            $haveDefaultButtonStyle={haveDefaultButtonStyle}
            disabled={trigger === Trigger.HOVER}
          >
            {button}
          </StyledPopoverButton>
          <Transition
            as={'div'}
            show={show}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <StyledPopoverPanel $panelstring={panelstring} anchor="bottom start">
              {panel}
            </StyledPopoverPanel>
          </Transition>
        </StyledPopoverEntry>
      )}
    </StyledPopover>
  );
};

const StyledPopover = ({ $fullWidth, $popoverstring: twStyle, className, ...props }: any) => (
  <DefaultPopover className={cn($fullWidth && 'w-full', twStyle, className)} {...props} />
);

const StyledPopoverEntry = ({ $fullWidth, className, ...props }: any) => (
  <div className={cn('w-fit', $fullWidth && 'w-full', className)} {...props} />
);

const StyledPopoverButton = ({
  $buttonstring: twstyle,
  $haveDefaultButtonStyle,
  $fullWidth,
  className,
  ...props
}: any) => (
  <DefaultPopover.Button
    className={cn(
      $haveDefaultButtonStyle &&
        'relative p-2 text-black bg-gray-50 text-base cursor-pointer rounded-md hover:bg-gray-100 hover:text-primary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-primary-100 focus:ring-white md:bg-transparent md:truncate md:w-1/2 md:text-sm lg:text-base lg:w-fit',
      $fullWidth && 'w-full',
      !$haveDefaultButtonStyle &&
        'focus-visible:border-none focus-visible:ring-0 focus-visible:shadow-none focus-visible:outline-none',
      twstyle,
      className
    )}
    {...props}
  />
);

const StyledPopoverPanel = ({ $panelstring: twStyle, className, ...props }: any) => (
  <DefaultPopover.Panel
    className={cn(
      '!flex w-fit p-6 bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
      twStyle,
      className
    )}
    {...props}
  />
);

Popover.Button = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

Popover.Panel = ({ children }: { children: React.ReactNode }) => {
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
